import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Box, Grid, Paper, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import { Save, PhotoCamera, Icecream } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  API_BASE_URL, PRODUCTO_FORMDATA, PRODUCTO_GET, PRODUCTO_ALTA, PRODUCTO_ACTUALIZAR
} from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import Cargando from './utils/Cargando.jsx';
import { avisarExito, avisarError } from '../services/toast';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Producto() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params] = useSearchParams();
  const id = params.get('id');
  const modo = id ? 'editar' : 'crear';
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [grupos, setGrupos] = useState([]);
  const [form, setForm] = useState({
    art_descripcion: '', art_descripcion_ticket: '',
    art_precio_normal: '', art_costo: '', art_peso: '', id_club_grido: '', grupo: '',
  });
  const [imagenActual, setImagenActual] = useState(null); // url relativa existente
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const fd = await axios.get(API_BASE_URL + PRODUCTO_FORMDATA, { headers: authHeader() });
      setGrupos(fd.data?.data?.grupos ?? []);
      if (id) {
        const res = await axios.get(API_BASE_URL + PRODUCTO_GET, { params: { id }, headers: authHeader() });
        const p = res.data?.data;
        if (p) {
          setForm({
            art_descripcion: p.art_descripcion ?? '',
            art_descripcion_ticket: p.art_descripcion_ticket ?? '',
            art_precio_normal: p.art_precio_normal ?? '',
            art_costo: p.art_costo ?? '',
            art_peso: p.art_peso ?? '',
            id_club_grido: p.id_club_grido ?? '',
            grupo: p.grupo ?? '',
          });
          setImagenActual(p.imagen_url || null);
        }
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar datos.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const guardar = async () => {
    setError('');
    if (!String(form.art_descripcion).trim() || !form.grupo) {
      setError('Descripción y grupo son requeridos.');
      return;
    }
    if (!form.id_club_grido) {
      setError('El ID Club Grido es requerido: identifica al producto en los registros.');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (file) fd.append('imagen', file);
    if (modo === 'editar') fd.append('id', id);
    try {
      const url = modo === 'editar' ? PRODUCTO_ACTUALIZAR : PRODUCTO_ALTA;
      const res = await axios.post(API_BASE_URL + url, fd, { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
      avisarExito(modo === 'editar' ? 'Producto actualizado.' : 'Producto creado.');
      setTimeout(() => navigate('/productos'), 900);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al guardar.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial /><Cargando />
      </Container>
    );
  }

  const previewUrl = preview || (imagenActual ? `${API_BASE_URL}${imagenActual}` : null);

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3, pb: 4 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Icecream}
          titulo={modo === 'editar' ? `Editar producto #${id}` : 'Nuevo producto'}
          subtitulo="Catálogo de productos"
          ayuda="El ID Club Grido identifica al producto en las ventas — es obligatorio y no puede repetirse entre productos."
        />
        <VolverBreadcrumb to="/productos" label="Productos" />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Imagen */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 140, height: 140, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  {previewUrl
                    ? <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Icecream sx={{ fontSize: 56, color: 'primary.main', opacity: 0.5 }} />}
                </Box>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
                <Button size="small" startIcon={<PhotoCamera />} onClick={() => fileRef.current?.click()}>
                  {previewUrl ? 'Cambiar' : 'Subir imagen'}
                </Button>
              </Box>
            </Grid>

            {/* Campos */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Grupo *</InputLabel>
                    <Select label="Grupo *" value={form.grupo} onChange={(e) => set('grupo', e.target.value)}>
                      {grupos.map((g) => <MenuItem key={g.grupo} value={g.grupo}>{g.grudescrip}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" type="number" label="Precio *" value={form.art_precio_normal}
                    onChange={(e) => set('art_precio_normal', e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField fullWidth size="small" label="Descripción *" value={form.art_descripcion}
                    onChange={(e) => set('art_descripcion', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Descripción comanda" value={form.art_descripcion_ticket}
                    onChange={(e) => set('art_descripcion_ticket', e.target.value)} />
                </Grid>

                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" type="number" label="Costo" value={form.art_costo}
                    onChange={(e) => set('art_costo', e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" type="number" label="Peso (kg)" value={form.art_peso}
                    onChange={(e) => set('art_peso', e.target.value)} inputProps={{ min: 0, step: '0.001' }} />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" type="number" label="ID Club Grido *" value={form.id_club_grido}
                    onChange={(e) => set('id_club_grido', e.target.value)} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" color="primary" size="large" onClick={guardar} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}>
              {submitting ? 'Guardando…' : (modo === 'editar' ? 'Guardar cambios' : 'Crear producto')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Producto;
